import React from 'react';
import { IonIcon, IonItem, IonLabel, IonText } from '@ionic/react';
import { chevronForwardOutline } from 'ionicons/icons';
import './ListItem.scss';
import { formatPhoneNumber } from 'core/util';

type ListItemProps = {
  title?: string;
  description?: string;
  url?: string;
  phone?: string;
  email?: string;
  startLabel?: string;
  startIcon?: string;
  endIcon?: string;
  href?: string;
  contactsView?: boolean;
  customView?: boolean;
  /** Merged with the default `IonItem` classes (e.g. `hk-alt-list-item` / `hk-list-item`). */
  className?: string;
  onClick?: (e: any) => void;
};

const ListItem: React.FC<ListItemProps> = ({
  title,
  description,
  url,
  phone,
  email,
  startIcon,
  startLabel,
  endIcon,
  href,
  contactsView,
  customView,
  className,
  onClick,
  children,
  ...rest
}) => {
  const customItemClass = ['hk-alt-list-item', className]
    .filter(Boolean)
    .join(' ');
  const mobileItemClass = ['hk-list-item', 'ion-margin-horizontal', className]
    .filter(Boolean)
    .join(' ');

  function CustomListItemView() {
    return (
      <IonItem
        lines="none"
        className={customItemClass}
        href={href}
        onClick={onClick}
        {...rest}
      >
        {children}
      </IonItem>
    );
  }

  function ContactsListItemView() {
    return (
      <IonItem
        lines="none"
        className="hk-alt-list-item"
        href={href}
        onClick={onClick}
        {...rest}
      >
        {startIcon && (
          <IonIcon
            slot="start"
            icon={startIcon}
            className="hk-alt-list-item-start-icon"
          />
        )}
        {startLabel && (
          <IonText slot="start" className="hk-alt-list-item-start-label">
            {startLabel}
          </IonText>
        )}
        <div className="hk-alt-list-item-label">
          <IonText className="hk-alt-list-item-title">
            <h2>{title}</h2>
          </IonText>
          <IonText className="hk-alt-list-item-description">
            <div>Website</div>
            <p>{url ? url : 'N/A'}</p>
          </IonText>
          <IonText className="hk-alt-list-item-description">
            <div>Contact Type</div>
            <p>{description ? description : 'N/A'}</p>
          </IonText>
          <IonText className="hk-alt-list-item-description">
            <div>Email</div>
            {!!email ? (
              email
                .split(',')
                .map((e) => e.trim())
                .filter(Boolean)
                .map((addr, i) => <p key={i}>{addr}</p>)
            ) : (
              <p>No email</p>
            )}
          </IonText>
          <div className="hk-alt-list-item-phone">
            <IonIcon
              slot="end"
              icon={endIcon ? endIcon : chevronForwardOutline}
              className="hk-alt-list-item-end-icon"
            ></IonIcon>
            <IonText className="hk-alt-list-item-phone-number">
              <div>Phone</div>
              {!!phone ? (
                <p>{formatPhoneNumber(phone)}</p>
              ) : (
                <p>No phone number</p>
              )}
            </IonText>
          </div>
        </div>
      </IonItem>
    );
  }

  function MobileListItemView() {
    return (
      <IonItem
        lines="none"
        className={mobileItemClass}
        href={href}
        onClick={onClick}
        {...rest}
      >
        {startIcon && (
          <IonIcon
            slot="start"
            icon={startIcon}
            className="hk-list-item-start-icon"
          />
        )}
        {startLabel && (
          <IonText slot="start" className="hk-list-item-start-label">
            {startLabel}
          </IonText>
        )}
        <IonLabel>
          <IonText color="dark">
            <h2 className="hk-list-item-title">{title}</h2>
          </IonText>
          <IonText color="medium">
            <p className="hk-list-item-description">{description}</p>
          </IonText>
        </IonLabel>
        <IonIcon
          slot="end"
          icon={endIcon ? endIcon : chevronForwardOutline}
          className="hk-list-item-end-icon"
        ></IonIcon>
      </IonItem>
    );
  }

  return (
    <>
      {contactsView === true && ContactsListItemView()}
      {customView === true && CustomListItemView()}
      {!contactsView && !customView && MobileListItemView()}
    </>
  );
};

export default ListItem;
